/**
 * Single source of truth for Owner + Super Admin sidebar (/owner/*).
 * Do not duplicate this nav in another file — see web-admin/OWNER_NAVIGATION.md
 */
import React, { useMemo, useEffect } from 'react';
import BrandWordmark from '../components/BrandWordmark';
import AppUpdateBanner from '../components/AppUpdateBanner';
import {
  Box,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
} from '@mui/material';
import { 
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  Logout as LogoutIcon,
  AttachMoney as SalesIcon,
  ListAlt as AuditLogsIcon,
  Terminal as LogsIcon,
  CreditCard as CreditManagementIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  ExpandLess,
  ExpandMore,
  Support as SupportIcon,
  SupervisorAccount as MultiBusinessIcon,
  SwapHoriz as TransferIcon,
  Security as DVLAAuditIcon,
  MenuBook as DocumentationIcon,
  Storage as DatabaseIcon,
  InsertChart as ReportsIcon,
} from '@mui/icons-material';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';

const OwnerLayout: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [openSettings, setOpenSettings] = React.useState(
    location.pathname === '/owner/settings' ||
    location.pathname.startsWith('/owner/settings/') ||
    location.pathname === '/owner/create-super-admin'
  );

  // Keep Settings submenu open when user is on settings or create-super-admin
  useEffect(() => {
    const path = location.pathname;
    if (
      path === '/owner/settings' ||
      path.startsWith('/owner/settings/') ||
      path === '/owner/create-super-admin'
    ) {
      setOpenSettings(true);
    }
  }, [location.pathname]);

  const handleLogout = () => {
    // Clear all auth-related items from localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('impersonatingBusiness');
    localStorage.removeItem('businessContext');
    localStorage.removeItem('businessName');
    localStorage.removeItem('businessId');
    localStorage.removeItem('superAdminToken');
    
    // Use window.location.href for a full page reload to ensure clean state
    window.location.href = '/login';
  };

  const navigateTo = (path: string) => {
    navigate(path);
  };

  const handleSettingsClick = () => {
    setOpenSettings(!openSettings);
  };

  // Documentation only for Super Admin. Hide when impersonating Test Business.
  const TEST_BUSINESS_ID = '01d96b55-9211-40e5-a249-137787baa921';
  const showDocumentation = useMemo(() => {
    try {
      const token = localStorage.getItem('token');
      const businessId = localStorage.getItem('businessId');
      if (!token) return false;
      const base64Url = token.split('.')[1];
      if (!base64Url) return false;
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) =>
        '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      ).join(''));
      const payload = JSON.parse(jsonPayload);
      const role = payload?.role;
      if (role !== 'Super Admin') return false;
      if (!businessId) return true;
      return businessId !== TEST_BUSINESS_ID;
    } catch {
      return false;
    }
  }, []);

  return (
    <Box sx={{ display: 'flex' }}>
      <AppUpdateBanner />
      {/* Sidebar */}
      <Box
        sx={{
          width: 240,
          flexShrink: 0,
          height: '100vh',
          bgcolor: 'background.paper',
          borderRight: 1,
          borderColor: 'divider',
          boxShadow: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Logo area */}
        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: 1, 
          borderColor: 'divider',
          height: 100,
        }}>
          <img 
            src={process.env.PUBLIC_URL + '/safety.png'} 
            alt="Safety Check Now Logo" 
            style={{ width: 48, height: 48, marginBottom: 8 }} 
          />
          <BrandWordmark variant="h6" compact sx={{ fontSize: '1.1rem', textAlign: 'center', width: '100%' }} />
        </Box>

        {/* Navigation items — scroll if viewport is short so all links stay reachable */}
        <List component="nav" sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 0 }}>
          <ListItem 
            button 
            onClick={() => navigateTo('/owner/dashboard')} 
            selected={location.pathname === '/owner/dashboard'}
          >
            <ListItemIcon>
              <DashboardIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Dashboard" />
          </ListItem>
          <ListItem 
            button 
            onClick={() => navigateTo('/owner/reports')}
            selected={location.pathname === '/owner/reports'}
          >
            <ListItemIcon>
              <ReportsIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Reports" />
          </ListItem>
          <ListItem 
            button 
            onClick={() => navigateTo('/owner/add-business')}
            selected={window.location.pathname === '/owner/add-business'}
          >
            <ListItemIcon>
              <BusinessIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Add Business" />
          </ListItem>
          <ListItem 
            button 
            onClick={() => navigateTo('/owner/business-management')}
            selected={window.location.pathname === '/owner/business-management'}
          >
            <ListItemIcon>
              <SettingsIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Business Management" />
          </ListItem>
          <ListItem 
            button 
            onClick={() => navigateTo('/owner/business-transfer-groups')}
            selected={window.location.pathname === '/owner/business-transfer-groups'}
          >
            <ListItemIcon>
              <TransferIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Transfer Groups" />
          </ListItem>
          <ListItem 
            button 
            onClick={() => navigateTo('/owner/multi-business-management')}
            selected={window.location.pathname === '/owner/multi-business-management'}
          >
            <ListItemIcon>
              <MultiBusinessIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Multi-Business Managers" />
          </ListItem>
          <ListItem 
            button 
            onClick={() => navigateTo('/owner/support-tickets')}
            selected={window.location.pathname === '/owner/support-tickets'}
          >
            <ListItemIcon>
              <SupportIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Support Tickets" />
          </ListItem>
          <ListItem 
            button 
            onClick={() => navigateTo('/owner/sales')}
            selected={window.location.pathname === '/owner/sales'}
          >
            <ListItemIcon>
              <SalesIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Sales" />
          </ListItem>
          <ListItem 
            button 
            onClick={() => navigateTo('/owner/audit-logs')}
            selected={window.location.pathname === '/owner/audit-logs'}
          >
            <ListItemIcon>
              <AuditLogsIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Audit Logs" />
          </ListItem>
          <ListItem 
            button 
            onClick={() => navigateTo('/owner/dvla-audit-logs')}
            selected={window.location.pathname === '/owner/dvla-audit-logs'}
          >
            <ListItemIcon>
              <DVLAAuditIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="DVLA Credit Usage" 
              secondary="Audit Logs"
              secondaryTypographyProps={{ fontSize: '0.7rem' }}
            />
          </ListItem>
          <ListItem 
            button 
            onClick={() => navigateTo('/owner/backend-logs')}
            selected={window.location.pathname === '/owner/backend-logs'}
          >
            <ListItemIcon>
              <LogsIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Backend Logs" />
          </ListItem>
          <ListItem 
            button 
            onClick={() => navigateTo('/owner/credit-management')}
            selected={window.location.pathname === '/owner/credit-management'}
          >
            <ListItemIcon>
              <CreditManagementIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Credit Management" />
          </ListItem>
          {showDocumentation && (
            <ListItem 
              button 
              onClick={() => navigateTo('/owner/documentation')}
              selected={location.pathname === '/owner/documentation'}
            >
              <ListItemIcon>
                <DocumentationIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Documentation" />
            </ListItem>
          )}
          <ListItem
            button
            onClick={() => navigateTo('/owner/settings?tab=database')}
            selected={
              location.pathname === '/owner/settings' &&
              new URLSearchParams(location.search).get('tab') === 'database'
            }
          >
            <ListItemIcon>
              <DatabaseIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Database Tools"
              secondary="Prod → Staging sync"
              secondaryTypographyProps={{ fontSize: '0.65rem', lineHeight: 1.2 }}
            />
          </ListItem>
          <ListItem 
            button 
            onClick={handleSettingsClick}
            selected={location.pathname === '/owner/create-super-admin'}
          >
            <ListItemIcon>
              <SettingsIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Settings" />
            {openSettings ? <ExpandLess /> : <ExpandMore />}
          </ListItem>
          <Collapse in={openSettings} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItem
                button
                onClick={() => navigateTo('/owner/settings?tab=database')}
                selected={
                  location.pathname === '/owner/settings' &&
                  new URLSearchParams(location.search).get('tab') === 'database'
                }
                sx={{ pl: 4 }}
              >
                <ListItemIcon>
                  <DatabaseIcon color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Database Tools"
                  secondary="Prod → Staging"
                  primaryTypographyProps={{ fontSize: '0.9rem' }}
                  secondaryTypographyProps={{ fontSize: '0.65rem' }}
                />
              </ListItem>
              <ListItem 
                button 
                onClick={() => navigateTo('/owner/create-super-admin')}
                selected={location.pathname === '/owner/create-super-admin'}
                sx={{ pl: 4 }}
              >
                <ListItemIcon>
                  <AddIcon color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Create Super Admin" 
                  primaryTypographyProps={{ fontSize: '0.9rem' }}
                />
              </ListItem>
            </List>
          </Collapse>
        </List>

        {/* Logout button at bottom */}
        <List>
          <ListItem
            button
            onClick={handleLogout}
            sx={{
              '&:hover': {
                backgroundColor: theme.palette.error.light,
                color: theme.palette.error.contrastText,
                '& .MuiListItemIcon-root': {
                  color: theme.palette.error.contrastText,
                },
              },
            }}
          >
            <ListItemIcon>
              <LogoutIcon color="error" />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItem>
        </List>
      </Box>

      {/* Main content area */}
      <Box component="main" sx={{ flexGrow: 1 }}>
        {/* Top Bar */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          height: 64,
          px: 2,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          boxShadow: 1
        }}>
          <Box sx={{ display: { sm: 'none' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img 
                src={process.env.PUBLIC_URL + '/safety.png'} 
                alt="Logo" 
                style={{ width: 32, height: 32, marginRight: 8 }} 
              />
              <BrandWordmark variant="h6" compact sx={{ fontSize: '1rem' }} />
            </Box>
          </Box>
        </Box>

        {/* Page content - will be filled by child routes */}
        <Outlet />
      </Box>
    </Box>
  );
};

export default OwnerLayout; 